import { BarChart } from "@mui/x-charts/BarChart";
import {
  Box,
  Container,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Fade,
  Grid,
  Badge,
  CircularProgress,
} from "@mui/material";
import { LineChart } from "@mui/x-charts";
import { useState, useEffect, useMemo } from "react";
import CircleIcon from "@mui/icons-material/Circle";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import Top5SkusTable from "../components/HomeComponents/TopSku.table";
import { useGetAllDashboardMetrics } from "../../client/hooks/dashboard/dashboard.queries";
import { useGetEcomerce } from "../../client/hooks/marketplace/marketplace.queries";
import moment from "moment/moment";

function HomePage() {
  const [insights, setInsights] = useState([
    {
      total_orders: 0,
      new_orders: 0,
      processing_orders: 0,
      cancelled_orders: 0,
    },
  ]);

  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isLive, setIsLive] = useState(false);
  const [activeRange, setActiveRange] = useState("7D");

  // Convert activeRange to params object for API calls
  const params = useMemo(() => {
    const rangeMap = {
      "7D": { period: "week" },
      "30D": { period: "month" },
      "3M": {
        period: "custom",
        startDate: moment().subtract(3, "months").toDate(),
      },
      "6M": {
        period: "custom",
        startDate: moment().subtract(6, "months").toDate(),
      },
      "12M": { period: "year" },
    };
    return rangeMap[activeRange] || { days: 7 };
  }, [activeRange]);

  // Fetch all dashboard data using a single query
  const {
    data: metricsData,
    isLoading: isLoadingMetrics,
    refetch: refetchMetrics,
  } = useGetAllDashboardMetrics(params);

  // Hook para buscar dados do marketplace
  const { data: marketplaceData } = useGetEcomerce();

  // Function for manual and auto refresh
  const updateDashboardData = () => {
    refetchMetrics();
    setLastUpdated(new Date());
  };

  // Update insights when order status data is available
  useEffect(() => {
    if (metricsData?.orderStatus) {
      setInsights([
        {
          total_orders: metricsData.orderStatus.summary?.total_orders || 0,
          new_orders: metricsData.orderStatus.summary?.new_orders || 0,
          processing_orders:
            metricsData.orderStatus.summary?.processing_orders || 0,
          cancelled_orders:
            metricsData.orderStatus.summary?.cancelled_orders || 0,
        },
      ]);
    }
  }, [metricsData]);

  // Simulate periodic updates when in live mode
  useEffect(() => {
    let interval;
    if (isLive) {
      interval = setInterval(() => {
        updateDashboardData();
      }, 30000); // Update every 30 seconds in live mode
    }
    return () => clearInterval(interval);
  }, [isLive]);

  // Load Material Symbols icons
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200";
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const listItemComponentOrders = (color, text, value) => {
    return (
      <ListItem
        sx={{
          py: 1,
          px: 2,
          "&:hover": {
            backgroundColor: "rgba(0,0,0,0.04)",
            borderRadius: 1,
          },
          mb: 0.5,
          transition: "all 0.2s ease",
        }}
      >
        <ListItemAvatar sx={{ minWidth: 40 }}>
          <Badge
            color="default"
            variant="dot"
            invisible={!isLive}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: "#4caf50",
                animation: isLive ? "pulse 2s infinite" : "none",
              },
            }}
          >
            <CircleIcon
              fontSize="small"
              sx={{
                color,
                boxShadow: 2,
                borderRadius: "100%",
                backgroundColor: color,
              }}
            />
          </Badge>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box display={"flex"} gap={1} alignItems="center">
              <Typography fontWeight={600} width={"30px"} variant="body2">
                {value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {text}
              </Typography>
            </Box>
          }
        />
      </ListItem>
    );
  };

  const dateRange = [
    {
      label: "7D",
      range: 7,
    },
    {
      label: "30D",
      range: 30,
    },
    {
      label: "3M",
      range: 90,
    },
    {
      label: "6M",
      range: 180,
    },
    {
      label: "12M",
      range: 360,
    },
  ];

  const handleRangeChange = (range) => {
    setActiveRange(range);
    // Trigger data reload with the new range after state is updated
    setTimeout(() => {
      updateDashboardData();
    }, 0);
  };

  const toggleLiveMode = () => {
    setIsLive(!isLive);
  };

  return (
    <Container
      sx={{
        py: 3,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        rowGap: 3,
        backgroundColor: "rgba(245, 247, 250, 0.7)", // Light background for better contrast
      }}
      maxWidth={false}
    >
      {/* Dashboard Header with Controls */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: 2, sm: 0 },
          mb: 2,
          bgcolor: "white",
          p: { xs: 2, md: 3 },
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "4px",
            background:
              "linear-gradient(90deg, #FF7322 0%, #ff9800 50%, #4caf50 100%)",
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.5,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Dashboard •{" "}
              {new Date().toLocaleDateString("pt-BR", {
                month: "long",
                year: "numeric",
              })}
            </Typography>
            {isLive && (
              <Chip
                label="Live"
                color="success"
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.65rem",
                  "& .MuiChip-label": { px: 1 },
                  animation: "fadeIn 0.5s ease-out",
                }}
              />
            )}
          </Box>
          <Typography variant="h5" fontWeight={600} align="left">
            Dashboard de Vendas
            <Box component="span" sx={{ color: "#FF7322", ml: 1 }}>
              {marketplaceData && marketplaceData[0]?.name ? marketplaceData[0].name : "WSW"}
            </Box>
          </Typography>
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={2}
          sx={{
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 2,
            px: 2,
            py: 1,
            backgroundColor: "rgba(0,0,0,0.01)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Status:
            </Typography>
            <Chip
              icon={isLive ? <AutorenewIcon /> : <RefreshIcon />}
              label={isLive ? "Live Mode" : "Manual Update"}
              color={isLive ? "success" : "default"}
              variant={isLive ? "filled" : "outlined"}
              onClick={toggleLiveMode}
              sx={{
                height: 32,
                fontWeight: 500,
                transition: "all 0.3s ease",
                "& .MuiChip-icon": {
                  animation: isLive ? "spin 2s linear infinite" : "none",
                },
              }}
            />
          </Box>

          <Tooltip title="Última atualização dos dados">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label={`${lastUpdated.toLocaleTimeString()}`}
                size="small"
                variant="outlined"
                sx={{ height: 32 }}
              />
              <IconButton
                size="small"
                onClick={updateDashboardData}
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: "rgba(0,0,0,0.04)",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.08)",
                  },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* KPI Summary Cards */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          mb: 3,
          p: { xs: 0, md: 0.5 },
          borderRadius: 2,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 12,
            left: 0,
            width: "4px",
            height: "25px",
            backgroundColor: "#FF7322",
            borderRadius: "0 4px 4px 0",
          },
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{
            pl: 2,
            mb: 2,
            color: "text.primary",
            textAlign: "left",
          }}
        >
          Resumo de Desempenho
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            width: "100%",
            gap: 2,
          }}
        >
          <Box
            sx={{
              flex: "1 1 calc(25% - 16px)",
              minWidth: "240px",
            }}
          >
            <StatCard
              title="Pedidos Hoje"
              value={metricsData?.kpis?.orders?.value || "0"}
              change={metricsData?.kpis?.orders?.change || "+0%"}
              trend={metricsData?.kpis?.orders?.trend || "up"}
              color="#FF7322"
              isLive={isLive}
              icon="shopping_cart"
              progressValue={metricsData?.kpis?.orders?.progressValue || 0}
              subtitle={`Meta: ${metricsData?.kpis?.orders?.goal || 0} pedidos`}
            />
          </Box>

          <Box
            sx={{
              flex: "1 1 calc(25% - 16px)",
              minWidth: "240px",
            }}
          >
            <StatCard
              title="Ticket Médio"
              value={metricsData?.kpis?.averageTicket?.value || "R$ 0,00"}
              change={metricsData?.kpis?.averageTicket?.change || "0%"}
              trend={metricsData?.kpis?.averageTicket?.trend || "up"}
              color="#2196f3"
              isLive={isLive}
              icon="payments"
              progressValue={
                metricsData?.kpis?.averageTicket?.progressValue || 0
              }
              subtitle={`Meta: ${metricsData?.kpis?.averageTicket?.goal || "R$ 0,00"}`}
            />
          </Box>

          <Box
            sx={{
              flex: "1 1 calc(25% - 16px)",
              minWidth: "240px",
            }}
          >
            <StatCard
              title="Faturamento"
              value={metricsData?.kpis?.revenue?.value || "R$ 0,00"}
              change={metricsData?.kpis?.revenue?.change || "0%"}
              trend={metricsData?.kpis?.revenue?.trend || "up"}
              color="#4caf50"
              isLive={isLive}
              icon="trending_up"
              progressValue={metricsData?.kpis?.revenue?.progressValue || 0}
              subtitle={`Meta: ${metricsData?.kpis?.revenue?.goal || "R$ 0,00"}`}
            />
          </Box>

          <Box
            sx={{
              flex: "1 1 calc(25% - 16px)",
              minWidth: "240px",
            }}
          >
            <StatCard
              title="Margem Média"
              value={metricsData?.kpis?.averageMargin?.value || "0%"}
              change={metricsData?.kpis?.averageMargin?.change || "0%"}
              trend={metricsData?.kpis?.averageMargin?.trend || "up"}
              color="#ff9800"
              isLive={isLive}
              icon="percent"
              progressValue={
                metricsData?.kpis?.averageMargin?.progressValue || 0
              }
              subtitle={`Meta: ${metricsData?.kpis?.averageMargin?.goal || "5%"}`}
            />
          </Box>
        </Box>
      </Box>

      {/* Order Status Section */}
      <Card
        sx={{
          width: "100%",
          mb: 3,
          overflow: "visible",
          boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
          borderRadius: 2,
          transition: "box-shadow 0.3s ease",
          "&:hover": { boxShadow: "0 5px 15px rgba(0,0,0,0.12)" },
        }}
      >
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={600}>
              Status dos Pedidos
            </Typography>
          }
          action={
            <Box display="flex" alignItems="center" gap={1}>
              {dateRange.map((range) => (
                <Chip
                  key={range.label}
                  label={range.label}
                  variant={activeRange === range.label ? "filled" : "outlined"}
                  color={activeRange === range.label ? "primary" : "default"}
                  size="small"
                  onClick={() => handleRangeChange(range.label)}
                  sx={{ minWidth: 45 }}
                />
              ))}
              <Tooltip title="Período customizado">
                <IconButton size="small">
                  <EditCalendarIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          }
          sx={{
            backgroundColor: "rgba(0,0,0,0.02)",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
          }}
        />

        <CardContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              width: "100%",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", md: "33.33%" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom align="left">
                    Pedidos de venda
                  </Typography>
                  <List>
                    {listItemComponentOrders(
                      "gray",
                      `Total de vendas`,
                      insights[0].total_orders
                    )}
                    {listItemComponentOrders(
                      "#4caf50",
                      `Novos`,
                      insights[0].new_orders
                    )}
                    {listItemComponentOrders(
                      "#2196f3",
                      `Em andamento`,
                      insights[0].processing_orders
                    )}
                    {listItemComponentOrders(
                      "#f44336",
                      `Cancelados`,
                      insights[0].cancelled_orders
                    )}
                  </List>
                </CardContent>
              </Card>
            </Box>
            <Box
              sx={{
                width: { xs: "100%", md: "66.67%" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <Typography variant="h6" align="left">
                      Quantidade Pedidos de Venda
                    </Typography>
                    <Typography fontWeight={700}>
                      {metricsData?.orderStatus?.summary?.total_orders || 0}{" "}
                      pedidos
                    </Typography>
                  </Box>
                  <BarChart
                    height={250}
                    grid={{ horizontal: true, vertical: false }}
                    borderRadius={6}
                    xAxis={[
                      {
                        scaleType: "band",
                        data: metricsData?.orderStatus?.chartData?.labels || [],
                        categoryGapRatio: 0.7,
                        barGapRatio: 0.4,
                        tickPlacement: "middle",
                      },
                    ]}
                    yAxis={[
                      {
                        valueFormatter: (value) =>
                          `${Intl.NumberFormat("en", { notation: "compact" }).format(value)}`,
                      },
                    ]}
                    series={[
                      {
                        data: metricsData?.orderStatus?.chartData?.data || [],
                        color: "#ff8266d9",
                      },
                    ]}
                  />
                </CardContent>
              </Card>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Performance Section */}
      <Card
        sx={{
          width: "100%",
          mb: 3,
          boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
          borderRadius: 2,
          transition: "box-shadow 0.3s ease",
          "&:hover": { boxShadow: "0 5px 15px rgba(0,0,0,0.12)" },
        }}
      >
        <CardHeader
          title={
            <Typography variant="h6" fontWeight={600}>
              Performance de Vendas
            </Typography>
          }
          action={
            <IconButton>
              <MoreVertIcon />
            </IconButton>
          }
          sx={{
            backgroundColor: "rgba(0,0,0,0.02)",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
          }}
        />

        <CardContent sx={{ pt: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              width: "100%",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", md: "50%" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  height: "100%",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Box>
                      <Typography variant="h6" align="left">
                        Valor Faturado
                      </Typography>
                      <Typography variant="h5" fontWeight={800} align="left">
                        {metricsData?.salesPerformance?.revenue?.total ||
                          "R$ 0,00"}
                      </Typography>
                    </Box>
                    <Chip
                      label={
                        metricsData?.salesPerformance?.revenue?.change ||
                        "0% vs mês anterior"
                      }
                      color={
                        metricsData?.salesPerformance?.revenue?.trend === "up"
                          ? "success"
                          : "error"
                      }
                      size="small"
                      icon={
                        metricsData?.salesPerformance?.revenue?.trend ===
                        "up" ? (
                          <TrendingUpIcon />
                        ) : (
                          <TrendingDownIcon />
                        )
                      }
                    />
                  </Box>
                  <BarChart
                    height={250}
                    grid={{ horizontal: true, vertical: false }}
                    borderRadius={6}
                    xAxis={[
                      {
                        scaleType: "band",
                        data:
                          metricsData?.salesPerformance?.revenue?.chartData
                            ?.labels || [],
                        categoryGapRatio: 0.6,
                        barGapRatio: 0.4,
                      },
                    ]}
                    yAxis={[
                      {
                        valueFormatter: (value) =>
                          `R$ ${Intl.NumberFormat("en", { notation: "compact" }).format(value)}`,
                      },
                    ]}
                    series={[
                      {
                        data:
                          metricsData?.salesPerformance?.revenue?.chartData
                            ?.data || [],
                        color: "#4caf50",
                      },
                    ]}
                  />
                </CardContent>
              </Card>
            </Box>

            <Box
              sx={{
                width: { xs: "100%", md: "50%" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Card variant="outlined">
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={1}
                  >
                    <Box>
                      <Typography variant="h6" align="left">
                        Ticket Médio
                      </Typography>
                      <Typography variant="h5" fontWeight={800} align="left">
                        {metricsData?.salesPerformance?.averageTicket?.value ||
                          "R$ 0,00"}
                      </Typography>
                    </Box>
                    <Chip
                      label={
                        metricsData?.salesPerformance?.averageTicket?.change ||
                        "0% vs mês anterior"
                      }
                      color={
                        metricsData?.salesPerformance?.averageTicket?.trend ===
                        "up"
                          ? "success"
                          : "error"
                      }
                      size="small"
                      icon={
                        metricsData?.salesPerformance?.averageTicket?.trend ===
                        "up" ? (
                          <TrendingUpIcon />
                        ) : (
                          <TrendingDownIcon />
                        )
                      }
                    />
                  </Box>
                  <LineChart
                    height={250}
                    grid={{ horizontal: true, vertical: false }}
                    xAxis={[
                      {
                        scaleType: "point",
                        data:
                          metricsData?.salesPerformance?.averageTicket
                            ?.chartData?.labels || [],
                      },
                    ]}
                    series={[
                      {
                        area: true,
                        data:
                          metricsData?.salesPerformance?.averageTicket
                            ?.chartData?.data || [],
                        color: "#2196f3",
                        showMark: false,
                      },
                    ]}
                  />
                </CardContent>
              </Card>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Marketplace Performance & Top SKUs */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: "41.67%" }, // Approximately equivalent to md={5} in Grid
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card
            sx={{
              height: "100%",
              boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
              borderRadius: 2,
              transition: "box-shadow 0.3s ease",
              "&:hover": { boxShadow: "0 5px 15px rgba(0,0,0,0.12)" },
            }}
          >
            <CardHeader
              title={
                <Typography variant="h6" fontWeight={600}>
                  Performance por Marketplace
                </Typography>
              }
              sx={{
                backgroundColor: "rgba(0,0,0,0.02)",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              }}
            />
            <CardContent sx={{ p: 0 }}>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  maxHeight: 400,
                  overflow: "auto",
                  border: "none",
                  borderRadius: 0,
                  "&::-webkit-scrollbar": {
                    width: "10px",
                    height: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(0,0,0,0.2)",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "rgba(0,0,0,0.05)",
                  },
                }}
              >
                <Table size="small" aria-label="marketplace performance">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "rgba(0,0,0,0.03)" }}>
                      <TableCell>Marketplace</TableCell>
                      <TableCell align="right">Vendas</TableCell>
                      <TableCell align="right">Margem</TableCell>
                      <TableCell align="right">%</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoadingMetrics ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={30} />
                        </TableCell>
                      </TableRow>
                    ) : (
                      (metricsData?.salesPerformance?.marketplaces || []).map(
                        (row, index) => (
                          <TableRow
                            key={row.mkplace || index}
                            sx={{
                              "&:nth-of-type(odd)": {
                                backgroundColor: "rgba(0,0,0,0.01)",
                              },
                              transition: "background-color 0.2s",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <TableCell component="th" scope="row">
                              {row.mkplace}
                            </TableCell>
                            <TableCell align="right">{row.venda}</TableCell>
                            <TableCell align="right">{row.equalmar}</TableCell>
                            <TableCell align="right">
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="flex-end"
                              >
                                {row.trend === "up" ? (
                                  <TrendingUpIcon
                                    fontSize="small"
                                    color="success"
                                    sx={{ mr: 0.5 }}
                                  />
                                ) : row.trend === "down" ? (
                                  <TrendingDownIcon
                                    fontSize="small"
                                    color="error"
                                    sx={{ mr: 0.5 }}
                                  />
                                ) : null}
                                {row.permar}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
        <Box
          sx={{
            width: { xs: "100%", md: "58.33%" }, // Approximately equivalent to md={7} in Grid
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card
            sx={{
              height: "100%",
              boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
              borderRadius: 2,
              transition: "box-shadow 0.3s ease",
              "&:hover": { boxShadow: "0 5px 15px rgba(0,0,0,0.12)" },
            }}
          >
            <CardHeader
              title={
                <Typography variant="h6" fontWeight={600}>
                  Top 5 SKUs
                </Typography>
              }
              sx={{
                backgroundColor: "rgba(0,0,0,0.02)",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              }}
            />
            <CardContent>
              <Top5SkusTable
                isLive={isLive}
                data={metricsData?.salesPerformance?.top5Skus || []}
                isLoading={isLoadingMetrics}
              />
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Add margin-analysis section or other sections as needed */}

      <style jsx global>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes pulse {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.3;
          }
          100% {
            opacity: 1;
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes glow {
          0% {
            box-shadow: 0 0 5px rgba(76, 175, 80, 0.5);
          }
          50% {
            box-shadow: 0 0 15px rgba(76, 175, 80, 0.7);
          }
          100% {
            box-shadow: 0 0 5px rgba(76, 175, 80, 0.5);
          }
        }
      `}</style>
    </Container>
  );
}

// Helper component for stat cards
function StatCard({
  title,
  value,
  change,
  trend,
  color,
  isLive,
  icon,
  progressValue,
  subtitle,
}) {
  return (
    <Card
      sx={{
        height: "100%",
        position: "relative",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
        },
        borderRadius: 2,
        border: "1px solid rgba(0,0,0,0.05)",
        background: `linear-gradient(135deg, #fff 0%, #fff 80%, ${color}08 100%)`,
        boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
        animation: "fadeIn 0.5s ease-out",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "3px",
          backgroundColor: color,
        }}
      />

      <CardContent
        sx={{ pb: "16px !important", position: "relative", zIndex: 1 }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {icon && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: `${color}15`,
                  color: color,
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 20 }}
                >
                  {icon}
                </span>
              </Box>
            )}
            <Typography variant="subtitle2" color="text.secondary" align="left">
              {title}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={change}
            color={trend === "up" ? "success" : "error"}
            icon={trend === "up" ? <TrendingUpIcon /> : <TrendingDownIcon />}
          />
        </Box>

        <Fade in={true} timeout={1000}>
          <Typography variant="h4" fontWeight={700} align="left" sx={{ mb: 1 }}>
            {value}
          </Typography>
        </Fade>

        {subtitle && (
          <Typography
            variant="caption"
            color="text.secondary"
            align="left"
            sx={{ display: "block", mb: 1 }}
          >
            {subtitle}
          </Typography>
        )}

        {progressValue !== undefined && (
          <Box sx={{ width: "100%", mt: 1 }}>
            <Box
              sx={{
                width: "100%",
                height: 4,
                backgroundColor: "rgba(0,0,0,0.09)",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${progressValue}%`,
                  backgroundColor: color,
                  transition: "width 1s ease-in-out",
                  borderRadius: 2,
                }}
              />
            </Box>
          </Box>
        )}
      </CardContent>

      {isLive && (
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: "#4caf50",
            animation: "pulse 2s infinite, glow 3s infinite",
          }}
        />
      )}
    </Card>
  );
}

export default HomePage;
